import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import {
  type CompetitionSeasonView,
  type MatchCandidateEventView,
  type MatchHomeAway,
  type MatchView,
  type SupportedLocale,
  type TeamView,
} from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';
import { apiRequest } from './api';
import { formatCalendarDate } from './athletes';
import { errorKeyOf, type ErrorKey } from './BillingForms';
import { registrableTeams } from './competitionRegistrations';
import { MatchRosterSection } from './MatchRosterSection';
import {
  cancelMatchRequest,
  candidateEventLabel,
  initialMatchForm,
  matchCorrectionFormOf,
  matchCorrectionRequest,
  matchHomeAwayKeys,
  matchPath,
  matchPhaseKeys,
  matchRequest,
  matchStatusKeys,
  maximumOpponentLabelLength,
  scheduledFirst,
  type MatchCorrectionForm,
  type MatchForm,
} from './matches';

export interface MatchesSectionProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}`. */
  base: string;
  csrfToken: string;
  competition: CompetitionSeasonView;
  /** The caller may browse athletes, so the roster of a match can be read. */
  canViewRoster: boolean;
}

type OpenPanel =
  | { kind: 'correct'; matchId: string; form: MatchCorrectionForm }
  | { kind: 'complete'; matchId: string }
  | { kind: 'cancel'; matchId: string; reason: string };

function homeAwayOf(text: string): MatchHomeAway {
  return text === 'away' ? 'away' : 'home';
}

/**
 * Matches of one competition. A registrar attaches a match event of the department to the
 * competition with the club's team, a phase, an opponent label and home or away; corrects it while
 * it is scheduled; marks it completed (which records the rule profile version that is active at that
 * moment) or cancels it with a reason. A completed or cancelled match stays as history.
 */
export function MatchesSection({
  locale,
  base,
  csrfToken,
  competition,
  canViewRoster,
}: MatchesSectionProps): React.JSX.Element {
  const [records, setRecords] = useState<MatchView[] | null>(null);
  const [events, setEvents] = useState<MatchCandidateEventView[]>([]);
  const [teams, setTeams] = useState<TeamView[]>([]);
  const [form, setForm] = useState<MatchForm>(() => initialMatchForm(competition));
  const [formKey, setFormKey] = useState(0);
  const [panel, setPanel] = useState<OpenPanel | null>(null);
  const [rosterMatchId, setRosterMatchId] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [message, setMessage] = useState<TranslationKey | null>(null);

  const load = useCallback(async () => {
    try {
      setRecords(
        await apiRequest<MatchView[]>(
          `${matchPath(base)}?competitionSeasonId=${encodeURIComponent(competition.id)}`,
        ),
      );
      if (competition.canManage) {
        setEvents(
          await apiRequest<MatchCandidateEventView[]>(
            `${matchPath(base)}/candidate-events?competitionSeasonId=${encodeURIComponent(competition.id)}`,
          ),
        );
      }
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base, competition.canManage, competition.id]);

  useEffect(() => {
    setRecords(null);
    setEvents([]);
    setPanel(null);
    setRosterMatchId(null);
    setErrorKey(null);
    setMessage(null);
    setForm(initialMatchForm(competition));
    setFormKey((current) => current + 1);
    void load();
  }, [load, competition]);

  useEffect(() => {
    if (!competition.canManage) return;
    void apiRequest<TeamView[]>(
      `${base}/teams?seasonId=${encodeURIComponent(competition.seasonId)}`,
    )
      .then((loaded) => {
        setTeams(registrableTeams(loaded, competition));
      })
      .catch((error: unknown) => {
        setErrorKey(errorKeyOf(error));
      });
  }, [base, competition]);

  async function submit(action: () => Promise<unknown>, successKey: TranslationKey): Promise<void> {
    setErrorKey(null);
    setMessage(null);
    try {
      await action();
      setMessage(successKey);
      setPanel(null);
      await load();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
      // A version conflict or a match closed meanwhile: show what the server holds now.
      await load();
    }
  }

  const createRequest = matchRequest(form, competition);

  async function record(): Promise<void> {
    if (createRequest === null) return;
    await submit(async () => {
      await apiRequest<MatchView>(matchPath(base), {
        method: 'POST',
        body: createRequest,
        csrfToken,
      });
      setForm(initialMatchForm(competition));
      setFormKey((current) => current + 1);
    }, 'match.done');
  }

  async function command(
    match: MatchView,
    request: { method: 'PATCH' | 'POST'; action?: 'complete' | 'cancel'; body?: unknown },
    successKey: TranslationKey,
  ): Promise<void> {
    await submit(async () => {
      await apiRequest<MatchView>(matchPath(base, match.id, request.action), {
        method: request.method,
        ...(request.body === undefined ? {} : { body: request.body }),
        csrfToken,
        ifMatchVersion: match.version,
      });
    }, successKey);
  }

  const closeButton = (
    <Button
      size="small"
      onClick={() => {
        setPanel(null);
      }}
    >
      {translate(locale, 'match.close')}
    </Button>
  );

  const phaseOptions = competition.phases.map((phase) => (
    <MenuItem key={phase} value={phase}>
      {translate(locale, matchPhaseKeys[phase])}
    </MenuItem>
  ));

  return (
    <Box component="section" aria-labelledby="matches-title" sx={{ mt: 3 }}>
      <Typography variant="h6" component="h3" id="matches-title" gutterBottom>
        {translate(locale, 'match.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate(locale, 'match.hint')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {message !== null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, message)}
        </Alert>
      )}
      {records === null && errorKey === null && (
        <Typography>{translate(locale, 'match.loading')}</Typography>
      )}
      {records?.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {translate(locale, 'match.empty')}
        </Typography>
      )}
      <Stack component="ul" spacing={2} sx={{ listStyle: 'none', padding: 0, margin: 0, mb: 3 }}>
        {scheduledFirst(records ?? []).map((match) => {
          const open = panel?.matchId === match.id ? panel : null;
          const correction =
            open?.kind === 'correct' ? matchCorrectionRequest(open.form, match) : null;
          const cancellation = open?.kind === 'cancel' ? cancelMatchRequest(open.reason) : null;
          return (
            <Box component="li" key={match.id}>
              <Typography variant="subtitle2">
                {match.team.name} – {match.opponentLabel} ·{' '}
                {translate(locale, matchStatusKeys[match.status])}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCalendarDate(match.event.date, locale)} {match.event.startTime} ·{' '}
                {match.event.title} · {translate(locale, matchHomeAwayKeys[match.homeAway])} ·{' '}
                {translate(locale, matchPhaseKeys[match.phase])}
              </Typography>
              {match.event.cancelled && match.status === 'scheduled' && (
                <Typography variant="body2" color="warning.main">
                  {translate(locale, 'match.eventCancelled')}
                </Typography>
              )}
              {match.status === 'completed' && (
                <Typography variant="body2" color="text.secondary">
                  {match.ruleProfile === null
                    ? translate(locale, 'match.noRuleVersion')
                    : `${translate(locale, 'match.ruleVersion')}: ${String(match.ruleProfile.versionNumber)}`}
                </Typography>
              )}
              {match.cancelReason !== null && (
                <Typography variant="body2" color="text.secondary">
                  {translate(locale, 'match.cancelReason')}: {match.cancelReason}
                </Typography>
              )}
              {canViewRoster && match.status !== 'cancelled' && rosterMatchId !== match.id && (
                <Box sx={{ mt: 0.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setRosterMatchId(match.id);
                    }}
                  >
                    {translate(locale, 'roster.open')}
                  </Button>
                </Box>
              )}
              {rosterMatchId === match.id && (
                <MatchRosterSection
                  locale={locale}
                  base={base}
                  csrfToken={csrfToken}
                  match={match}
                  onClose={() => {
                    setRosterMatchId(null);
                  }}
                />
              )}
              {match.canManage && match.status === 'scheduled' && open === null && (
                <Box sx={{ mt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setPanel({ kind: 'complete', matchId: match.id });
                    }}
                  >
                    {translate(locale, 'match.complete')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setPanel({
                        kind: 'correct',
                        matchId: match.id,
                        form: matchCorrectionFormOf(match),
                      });
                    }}
                  >
                    {translate(locale, 'match.correct')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setPanel({ kind: 'cancel', matchId: match.id, reason: '' });
                    }}
                  >
                    {translate(locale, 'match.cancel')}
                  </Button>
                </Box>
              )}
              {open?.kind === 'complete' && (
                <Stack
                  component="form"
                  spacing={1.5}
                  sx={{ mt: 1, maxWidth: 420 }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    void command(
                      match,
                      { method: 'POST', action: 'complete' },
                      'match.complete.done',
                    );
                  }}
                >
                  <Typography variant="body2">
                    {translate(locale, 'match.complete.hint')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button type="submit" variant="contained" size="small">
                      {translate(locale, 'match.complete.submit')}
                    </Button>
                    {closeButton}
                  </Box>
                </Stack>
              )}
              {open?.kind === 'cancel' && (
                <Stack
                  component="form"
                  spacing={1.5}
                  sx={{ mt: 1, maxWidth: 420 }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (cancellation !== null) {
                      void command(
                        match,
                        { method: 'POST', action: 'cancel', body: cancellation },
                        'match.cancel.done',
                      );
                    }
                  }}
                >
                  <Typography variant="body2">{translate(locale, 'match.cancel.hint')}</Typography>
                  <TextField
                    required
                    size="small"
                    label={translate(locale, 'match.cancel.reason')}
                    value={open.reason}
                    onChange={(event) => {
                      setPanel({ ...open, reason: event.target.value });
                    }}
                    slotProps={{ htmlInput: { maxLength: 500 } }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="error"
                      size="small"
                      disabled={cancellation === null}
                    >
                      {translate(locale, 'match.cancel.submit')}
                    </Button>
                    {closeButton}
                  </Box>
                </Stack>
              )}
              {open?.kind === 'correct' && (
                <Stack
                  component="form"
                  spacing={1.5}
                  sx={{ mt: 1, maxWidth: 420 }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (correction !== null) {
                      void command(
                        match,
                        { method: 'PATCH', body: correction },
                        'match.correct.done',
                      );
                    }
                  }}
                >
                  <TextField
                    required
                    size="small"
                    label={translate(locale, 'match.field.opponent')}
                    value={open.form.opponentLabel}
                    onChange={(event) => {
                      setPanel({
                        ...open,
                        form: { ...open.form, opponentLabel: event.target.value },
                      });
                    }}
                    slotProps={{ htmlInput: { maxLength: maximumOpponentLabelLength } }}
                  />
                  <TextField
                    required
                    select
                    size="small"
                    label={translate(locale, 'match.field.homeAway')}
                    value={open.form.homeAway}
                    onChange={(event) => {
                      setPanel({
                        ...open,
                        form: { ...open.form, homeAway: homeAwayOf(event.target.value) },
                      });
                    }}
                  >
                    <MenuItem value="home">{translate(locale, 'match.homeAway.home')}</MenuItem>
                    <MenuItem value="away">{translate(locale, 'match.homeAway.away')}</MenuItem>
                  </TextField>
                  <TextField
                    required
                    select
                    size="small"
                    label={translate(locale, 'match.field.phase')}
                    value={open.form.phase}
                    onChange={(event) => {
                      const phase = competition.phases.find(
                        (known) => known === event.target.value,
                      );
                      if (phase !== undefined) {
                        setPanel({ ...open, form: { ...open.form, phase } });
                      }
                    }}
                  >
                    {phaseOptions}
                  </TextField>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="small"
                      disabled={correction === null}
                    >
                      {translate(locale, 'match.correct.submit')}
                    </Button>
                    {closeButton}
                  </Box>
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
      {competition.canManage ? (
        <Stack
          component="form"
          key={formKey}
          spacing={2}
          sx={{ maxWidth: 480 }}
          onSubmit={(event) => {
            event.preventDefault();
            void record();
          }}
        >
          <Typography variant="subtitle1" component="h4">
            {translate(locale, 'match.create.title')}
          </Typography>
          {events.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {translate(locale, 'match.noEvents')}
            </Typography>
          )}
          {teams.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {translate(locale, 'match.noTeams')}
            </Typography>
          )}
          <TextField
            required
            select
            size="small"
            label={translate(locale, 'match.field.event')}
            value={form.eventId}
            onChange={(event) => {
              setForm({ ...form, eventId: event.target.value });
            }}
          >
            {events.map((candidate) => (
              <MenuItem key={candidate.id} value={candidate.id}>
                {candidateEventLabel(candidate, locale)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            select
            size="small"
            label={translate(locale, 'match.field.team')}
            value={form.teamId}
            onChange={(event) => {
              setForm({ ...form, teamId: event.target.value });
            }}
          >
            {teams.map((team) => (
              <MenuItem key={team.id} value={team.id}>
                {team.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            select
            size="small"
            label={translate(locale, 'match.field.phase')}
            value={form.phase}
            onChange={(event) => {
              const phase = competition.phases.find((known) => known === event.target.value);
              if (phase !== undefined) setForm({ ...form, phase });
            }}
          >
            {phaseOptions}
          </TextField>
          <TextField
            required
            size="small"
            label={translate(locale, 'match.field.opponent')}
            helperText={translate(locale, 'match.field.opponentHint')}
            value={form.opponentLabel}
            onChange={(event) => {
              setForm({ ...form, opponentLabel: event.target.value });
            }}
            slotProps={{ htmlInput: { maxLength: maximumOpponentLabelLength } }}
          />
          <TextField
            required
            select
            size="small"
            label={translate(locale, 'match.field.homeAway')}
            value={form.homeAway}
            onChange={(event) => {
              setForm({ ...form, homeAway: homeAwayOf(event.target.value) });
            }}
          >
            <MenuItem value="home">{translate(locale, 'match.homeAway.home')}</MenuItem>
            <MenuItem value="away">{translate(locale, 'match.homeAway.away')}</MenuItem>
          </TextField>
          <Box>
            <Button type="submit" variant="contained" disabled={createRequest === null}>
              {translate(locale, 'match.submit')}
            </Button>
          </Box>
        </Stack>
      ) : null}
    </Box>
  );
}
